import {z} from "zod";

import type {ForgotRequest, LoginRequest, LogoutResponse, ResetRequest, Session} from "@/src/modules/auth/types";
import CoreService from "@/src/modules/core/services/core";

import {impersonationService} from "@/src/modules/support/services/impersonation";
import {clearGlitchTipUser} from "@/src/modules/core/lib/glitchtip";
import {useMeStore} from "@/src/modules/core/store/me";
import {purgeHelpCacheForUser} from "@/src/modules/help/lib/help-cache";

import {authApi} from "../api";
import {loginSchema} from "../schemas";
import {parseLogoutResponse} from "../lib/parse-logout-response";
import {useSessionEndStore} from "../store/session-end";
import {useSessionStore} from "../store/session";

class AuthService extends CoreService {
  async login(credentials: LoginRequest): Promise<Session> {
    const {data: session} = await authApi.login(credentials);

    useSessionStore.setState({
      session,
    });

    useSessionEndStore.getState().reset();

    return session;
  }

  async logout(): Promise<LogoutResponse> {
    const userId = useMeStore.getState().me?.user_id;
    if (userId) {
      purgeHelpCacheForUser(userId);
    }

    const {data} = await authApi.logout();
    const result = parseLogoutResponse(data);

    impersonationService.clearOnLogout();
    clearGlitchTipUser();

    useSessionStore.setState({
      session: null,
    });

    // Always clear this device. `logout_scope` only indicates whether other
    // tabs/devices receive `session:invalidated` (`all_devices`) or not
    // (`current_device` — Admin voluntary logout).
    return result;
  }

  async forgot(data: ForgotRequest): Promise<void> {
    await authApi.forgot(data);
  }

  async reset(data: ResetRequest): Promise<void> {
    await authApi.reset(data);
  }

  clearSession(): void {
    clearGlitchTipUser();

    useSessionStore.setState({
      session: null,
    });
  }
}

export const authService = new AuthService();
